'use strict';
import PropTypes from 'external/prop-types';
import L from 'external/leaflet';
import '../css/orthophoto-plugin.scss';
import LogicalLayerActions from '../actions/LogicalLayerActions';
import { getLogicalLayerStore } from '../stores/StoreFactory';
import { AppContext } from 'plugin/liferay-widgets-react-web';

const LogicalLayerStore = getLogicalLayerStore(AppContext);

const DATA_LOGICAL_LAYERS = 'LOGICAL_LAYER_LIST';
const ORTHO_PHOTO_LAYER_ID = 'lgl_wms_dop_aerialmap';
const OMNISCALE_ROADS_LABELS_LAYER_ID = 'omniscale_roads_labels';

export default class OrthoPhotoPlugin {
    constructor(props, config) {
        this.config = config;
        this.props = props;

        this.hasTileError = false;
        this.interval = setInterval(() => {this.hasTileError = false;}, 6000);

        this.toggleActive = this.toggleActive.bind(this);
        this.onTileError = this.onTileError.bind(this);
        this.onLayersLoad = this.onLayersLoad.bind(this);
    }

    initOptions(o) {
        return o;
    }

    init(map, o) {
        this._map = map;
        LogicalLayerActions.allViewableGroupedList(DATA_LOGICAL_LAYERS);
        this.subscription = LogicalLayerStore.addListener(this.onLayersLoad.bind(this));

        this.customBaseLayerControl = new CustomBaseLayersControl(this.toggleActive);
        map.addControl(this.customBaseLayerControl);
    }

    options(o) {
        this._options = o;
        this._map.eachLayer(l => {
            if (l.logicalLayerId === this._options.defaultBaseLayerId) {
                this.baseLayer = l;
            }
        });
        return o;
    }

    onLayersLoad() {
        const list = LogicalLayerStore.getRemoteData(DATA_LOGICAL_LAYERS);
        if (list && list._count > 0) {
            const bwDopLayerDefinition = list.item.find(item => item.uuid === ORTHO_PHOTO_LAYER_ID);
            const omniscaleLayerDefinition = list.item.find(item => item.uuid === OMNISCALE_ROADS_LABELS_LAYER_ID);

            if (bwDopLayerDefinition) {
                try {
                    const config = JSON.parse(bwDopLayerDefinition.children[0].config);
                    this.bwDopLayer = L.tileLayer.wms(config.url, config);
                } catch (e) {
                    console.error('Failed to JSON parse bwDopLayerDefinition config', bwDopLayerDefinition);
                }
            }
            if (omniscaleLayerDefinition) {
                try {
                    const config = JSON.parse(omniscaleLayerDefinition.children[0].config);
                    this.omniscaleFeatureLayer = L.tileLayer.wms(config.url, config.config);
                } catch (e) {
                    console.error('Failed to JSON parse omniscaleLayerDefinition config', bwDopLayerDefinition);
                }
            }
        }
    }

    onTileError(e) {
        console.info('TILE ERROR', e);
        if (!this.hasTileError) {
            this.hasTileError = true;
            this._map.toast.warning('', 'Es kann zu Problemen beim Abruf der Satellitenbilder kommen.', { timeout: 10000 });
        }
    }

    toggleActive(type) {
        if (type === 'map') {
            this._map.removeLayer(this.omniscaleFeatureLayer);
            this.bwDopLayer.off('tileerror', this.onTileError);
            this._map.removeLayer(this.bwDopLayer);
            if (this.baseLayer) {
                this._map.addLayer(this.baseLayer);
                this.baseLayer.bringToBack();
            }
            return;
        }
        if (this.baseLayer) {
            this._map.removeLayer(this.baseLayer);
        }
        this.bwDopLayer.on('tileerror', this.onTileError);
        this._map.addLayer(this.bwDopLayer);
        this._map.addLayer(this.omniscaleFeatureLayer);
        this.omniscaleFeatureLayer.bringToBack();
        this.bwDopLayer.bringToBack();
    }

    remove() {
        this._map = null;
        this.baseLayer = null;
        if (this.subscription) {
            this.subscription.remove();
        }
    }
}

OrthoPhotoPlugin.contextTypes = {
    contextId: PropTypes.string
};

export const CustomBaseLayersControl = L.Control.extend({
    options: {
        collapsed: true,
        position: 'bottomleft',
    },

    initialize(onClick, options) {
        L.Util.setOptions(this, options);
        this.onClick = onClick;
    },

    onAdd(map) {
        this._initLayout();
        this._map = map;
        if (!this.options.collapsed) {
            // update the height of the container after resizing the window
            map.on('resize', this._expandIfNotCollapsed, this);
        }
        return this._container;
    },

    addTo(map) {
        L.Control.prototype.addTo.call(this, map);
        // Trigger expand after Layers Control has been inserted into DOM so that is now has an actual height.
        return this._expandIfNotCollapsed();
    },

    onRemove() {
        this._map.off('resize', this._expandIfNotCollapsed, this);
        L.DomEvent.off(this.baseMapButton);
        L.DomEvent.off(this.satelliteButton);
    },

    expand() {
        this._container.classList.add('leaflet-control-layers-expanded');
        this._section.style.height = null;
        const acceptableHeight = this._map.getSize().y - (this._container.offsetTop + 50);
        if (acceptableHeight < this._section.clientHeight) {
            this._section.classList.add('leaflet-control-layers-scrollbar');
            this._section.style.height = `${acceptableHeight}px`;
        } else {
            this._section.classList.remove('leaflet-control-layers-scrollbar');
        }
        return this;
    },

    // @method collapse(): this
    // Collapse the control container if expanded.
    collapse(ev) {
        // On touch devices `pointerleave` is fired while clicking on a checkbox.
        // The control was collapsed instead of adding the layer to the map.
        // So we allow collapse if it is not touch and pointerleave.
        if (!ev || !(ev.type === 'pointerleave' && ev.pointerType === 'touch')) {
            this._container.classList.remove('leaflet-control-layers-expanded');
        }
        return this;
    },

    _initLayerButton(className, title, type) {
        const btn = L.DomUtil.create('div', `leaflet-control-orthophoto-btn ${className}`);
        btn.setAttribute('role', 'button');
        btn.setAttribute('title', title);
        const label = L.DomUtil.create('span', 'leaflet-control-orthophoto-btn-label', btn);
        label.innerHTML = title;
        L.DomEvent.on(btn, 'click', this._onClick.bind(this, type));
        return btn;
    },

    _onClick(type, e) {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);
        if (type === 'map') {
            this.baseMapButton.classList.add('active');
            this.satelliteButton.classList.remove('active');
        } else {
            this.baseMapButton.classList.remove('active');
            this.satelliteButton.classList.add('active');
        }
        if (this.onClick) {
            this.onClick(type);
        }
    },

    _initLayout() {
        const className = 'leaflet-control-layers',
            container = this._container = L.DomUtil.create('div', `${className} leaflet-control-orthophoto`),
            collapsed = this.options.collapsed;

        // makes this work on IE touch devices by stopping it from firing a mouseout event when the touch is released
        container.setAttribute('aria-haspopup', true);

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        const section = this._section = L.DomUtil.create('fieldset', `${className}-list`);
        this.baseMapButton = this._initLayerButton('leaflet-control-orthophoto-basemap-btn active', 'Karte', 'map');
        this.satelliteButton = this._initLayerButton('leaflet-control-orthophoto-satellite-btn', 'Satellit', 'satellite');
        section.appendChild(this.baseMapButton);
        section.appendChild(this.satelliteButton);

        if (collapsed) {
            this._map.on('click', this.collapse, this);

            L.DomEvent.on(container, {
                pointerenter: this._expandSafely,
                pointerleave: this.collapse
            }, this);
        }
        const link = this._layersLink = L.DomUtil.create('a', `${className}-toggle`, container);
        L.DomUtil.create('span', 'icon icon-fliwas-layer', link);
        link.href = '#';
        link.title = 'Layers';
        link.setAttribute('role', 'button');

        L.DomEvent.on(link, {
            keydown(e) {
                if (e.code === 'Enter') {
                    this._expandSafely();
                }
            },
            // Certain screen readers intercept the key event and instead send a click event
            click(e) {
                L.DomEvent.preventDefault(e);
                this._expandSafely();
            }
        }, this);

        if (!collapsed) {
            this.expand();
        }
        container.appendChild(section);
    },

    _expandIfNotCollapsed() {
        if (this._map && !this.options.collapsed) {
            this.expand();
        }
        return this;
    },

    _expandSafely() {
        const section = this._section;
        L.DomEvent.on(section, 'click', L.DomEvent.preventDefault);
        this.expand();
        setTimeout(() => {
            L.DomEvent.off(section, 'click', L.DomEvent.preventDefault);
        });
    },
});




// WEBPACK FOOTER //
// ./js/plugins/OrthoPhotoPlugin.js